import { z } from 'zod';
import { FIELD_OPERATORS, applyOperator, readPath, } from '@wit-support/pipeline';
/**
 * Domain of a reaction rule's `field` condition: the flat, denormalized ROW
 * shape a rule reads from, not the underlying tables. `case` is the
 * `risk_case` row (or its equivalent projection) and `alert` is the specific
 * `risk_alert` that TRIGGERED the rule evaluation — a case can carry many
 * alerts, but a reaction always fires off one concrete inbound alert.
 */
export const RISK_FIELD_SOURCES = ['case', 'alert'];
const STRING_OPS = [
    'exists',
    'not_exists',
    'equals',
    'not_equals',
    'contains',
    'in',
];
const NUMBER_OPS = [
    'exists',
    'not_exists',
    'equals',
    'not_equals',
    'gt',
    'gte',
    'lt',
    'lte',
    'in',
];
const stringField = (path, label) => ({
    path,
    label,
    type: 'string',
    operators: STRING_OPS,
});
const numberField = (path, label) => ({
    path,
    label,
    type: 'number',
    operators: NUMBER_OPS,
});
/** Fields readable off the `case` source (a `risk_case` row, flattened). */
export const RISK_CASE_FIELDS = [
    stringField('kind', 'Тип кейса'),
    numberField('amount_cents', 'Сумма (центы)'),
    stringField('currency', 'Валюта'),
    stringField('match_confidence', 'Уверенность совпадения'),
    stringField('match_basis', 'Основание совпадения'),
    stringField('card_last4', 'Последние 4 цифры карты'),
];
/** Fields readable off the `alert` source (the triggering `risk_alert` row, flattened). */
export const RISK_ALERT_FIELDS = [
    stringField('network', 'Сеть'),
    stringField('kind', 'Тип алерта'),
    stringField('reason_code', 'Код причины'),
    stringField('card_brand', 'Бренд карты'),
    stringField('country', 'Страна'),
    numberField('amount_cents', 'Сумма (центы)'),
];
/** The full catalog, keyed by source. The schema + evaluator both index this. */
export const RISK_FIELD_CATALOG = {
    case: RISK_CASE_FIELDS,
    alert: RISK_ALERT_FIELDS,
};
/** Look up the `FieldDef` for a `(source, path)` pair, or `undefined` if unknown. */
const findRiskFieldDef = (source, path) => RISK_FIELD_CATALOG[source].find((f) => f.path === path);
const MAX_REACTION_DEPTH = 20;
const MAX_REACTION_NODES = 1000; // defense-in-depth against wide-but-shallow abuse
/**
 * Iterative bounded pre-check — NEVER recurses on the JS call stack. Copied
 * (structure + comment intent, adapted to this tree's leaf kinds) from the
 * unexported `triggerStructureGuard` in `packages/pipeline/src/trigger.ts:49-74`,
 * since that function is private to the pipeline package and this module has
 * its own, differently-shaped recursive tree. Walks the raw blob with an
 * explicit stack, capping depth (≤MAX_REACTION_DEPTH) and total node count
 * (≤MAX_REACTION_NODES) BEFORE the recursive Zod parse, so a pathological
 * deep/wide tree fails with a clean ZodError instead of a RangeError. Only
 * descends the structural `of` key; full shape validation is left to the
 * per-branch schema below.
 */
const reactionStructureGuard = (raw, ctx) => {
    const stack = [{ node: raw, depth: 1 }];
    let nodes = 0;
    while (stack.length > 0) {
        const { node, depth } = stack.pop();
        if (node === null || typeof node !== 'object')
            continue;
        nodes += 1;
        if (nodes > MAX_REACTION_NODES) {
            ctx.addIssue({
                code: 'custom',
                message: `Условие слишком большое (>${MAX_REACTION_NODES} узлов)`,
            });
            return;
        }
        if (depth > MAX_REACTION_DEPTH) {
            ctx.addIssue({
                code: 'custom',
                message: `Условие слишком глубокое (>${MAX_REACTION_DEPTH})`,
            });
            return;
        }
        const of = node.of;
        if (Array.isArray(of)) {
            for (const child of of)
                stack.push({ node: child, depth: depth + 1 });
        }
        else if (of !== undefined) {
            stack.push({ node: of, depth: depth + 1 });
        }
    }
};
const riskReactionExprBase = z.lazy(() => z.union([
    z.object({ type: z.literal('always') }),
    z
        .object({
        type: z.literal('field'),
        source: z.enum(RISK_FIELD_SOURCES),
        path: z.string().min(1, 'Выберите поле'),
        operator: z.enum(FIELD_OPERATORS),
        value: z.unknown().optional(),
    })
        .superRefine((node, ctx) => {
        const def = findRiskFieldDef(node.source, node.path);
        if (!def) {
            ctx.addIssue({
                code: 'custom',
                path: ['path'],
                message: `Неизвестное поле «${node.path}» для источника «${node.source}»`,
            });
            return;
        }
        if (!def.operators.includes(node.operator)) {
            ctx.addIssue({
                code: 'custom',
                path: ['operator'],
                message: `Оператор «${node.operator}» не применим к полю «${node.path}»`,
            });
        }
    }),
    z.object({
        type: z.literal('all'),
        of: z.array(riskReactionExprBase).min(1, 'Добавьте условие'),
    }),
    z.object({
        type: z.literal('any'),
        of: z.array(riskReactionExprBase).min(1, 'Добавьте условие'),
    }),
    z.object({ type: z.literal('not'), of: riskReactionExprBase }),
]));
export const RiskReactionExprSchema = z
    .unknown()
    .superRefine(reactionStructureGuard)
    .pipe(riskReactionExprBase);
export const RiskReactionStepsConfigSchema = z.object({
    refund: z.boolean(),
    cancelSubscription: z.boolean(),
    respondAlert: z.boolean(),
});
/**
 * Evaluate a `RiskReactionExpr` against a case/alert pair. Assumes the
 * expression already passed `RiskReactionExprSchema` (every `field` leaf
 * names a known `(source, path)`); an expression that slipped through some
 * other path with an unknown field is treated as non-matching rather than
 * thrown, matching `applyOperator`'s total/never-throws contract.
 */
export const evaluateReactionExpr = (expr, ctx) => {
    switch (expr.type) {
        case 'always':
            return true;
        case 'field': {
            const def = findRiskFieldDef(expr.source, expr.path);
            if (!def)
                return false;
            const actual = readPath(ctx[expr.source], expr.path);
            return applyOperator(expr.operator, actual, expr.value, def.type);
        }
        case 'all':
            return expr.of.every((e) => evaluateReactionExpr(e, ctx));
        case 'any':
            return expr.of.some((e) => evaluateReactionExpr(e, ctx));
        case 'not':
            return !evaluateReactionExpr(expr.of, ctx);
    }
};
//# sourceMappingURL=reaction.js.map